import { XMarkIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import DemoUpdateModal from './DemoUpdateModal';
import DemoResetModal from '../admin/DemoResetModal';

export default function DemoDetailsModal({ demo: propDemo, onClose: propOnClose, onUpdate }) {
  const { demoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [demo, setDemo] = useState(propDemo);
  const [loading, setLoading] = useState(!propDemo);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);
  const [hostInfo, setHostInfo] = useState(null);
  // State to control the main modal visibility
  const [isOpen, setIsOpen] = useState(true);
  // State to control the update modal visibility
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  // State to control the reset modal visibility
  const [showResetModal, setShowResetModal] = useState(false);
  
  // If no demo is passed as prop, fetch it using the demoId from URL
  useEffect(() => {
    if (!propDemo && demoId) {
      const fetchDemo = async () => {
        try {
          setLoading(true);
          const demoDoc = await getDoc(doc(db, 'demo_bookings', demoId));
          if (demoDoc.exists()) {
            setDemo({ id: demoDoc.id, ...demoDoc.data() });
          }
        } catch (error) {
          console.error('Error fetching demo:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchDemo();
    }
  }, [demoId, propDemo]);
  
  // Fetch agent information
  useEffect(() => {
    const fetchAgentInfo = async () => {
      const agentCode = demo?.payload?.agentCode;
      if (!agentCode) {
        return;
      }
      
      try {
        const agentsRef = collection(db, 'plsss_agents');
        const q = query(agentsRef, where('agent_id', '==', agentCode), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const agentData = querySnapshot.docs[0].data();
          setAgentInfo({
            code: agentData.agent_id || agentCode,
            name: agentData.name || 'Unknown'
          });
        }
      } catch (error) {
        console.error('Error fetching agent info:', error);
      }
    };
    
    if (demo) {
      fetchAgentInfo();
    }
  }, [demo]);
  
  // Fetch referrer information if demo has a referrer field
  useEffect(() => {
    const fetchReferrerInfo = async () => {
      const referrerId = demo?.payload?.referrer;
      if (!referrerId) {
        return;
      }
      
      try {
        // Query by agent_id field instead of document ID
        const agentsRef = collection(db, 'plsss_agents');
        const q = query(agentsRef, where('agent_id', '==', referrerId), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const agentData = querySnapshot.docs[0].data();
          setReferrerInfo({
            code: agentData.agent_id || referrerId,
            name: agentData.name || 'Unknown'
          });
        }
      } catch (error) {
        console.error('Error fetching referrer info:', error);
      }
    };
    
    if (demo) {
      fetchReferrerInfo();
    }
  }, [demo]);
  
  // Fetch host information if demo is hosted
  useEffect(() => {
    const fetchHostInfo = async () => {
      const hostCode = demo?.payload?.hostedDemo?.hostCode;
      if (!hostCode || hostedDemo?.isHosted !== 'YES') {
        console.log('No host code found or demo is not hosted');
        return;
      }
      
      console.log('Fetching host info for agent_id:', hostCode);
      
      try {
        const agentsRef = collection(db, 'plsss_agents');
        const q = query(agentsRef, where('agent_id', '==', hostCode), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const agentData = querySnapshot.docs[0].data();
          console.log('Found host:', agentData);
          setHostInfo({
            code: agentData.agent_id || hostCode,
            name: agentData.name || 'Unknown'
          });
        } else {
          console.log('No agent found with agent_id:', hostCode);
        }
      } catch (error) {
        console.error('Error fetching host info:', error);
      }
    };
    
    if (demo) {
      fetchHostInfo();
    }
  }, [demo]);
  
  // Handle modal close
  const handleClose = (e) => {
    e?.stopPropagation?.();
    
    // Close the modal
    setIsOpen(false);
    
    // Call the onClose prop if provided
    if (propOnClose) {
      propOnClose();
    }
    
    // Navigate back to my-demos if we're in a demo details route
    if (location.pathname.includes('/demos/')) {
      navigate('/my-demos');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-2xl w-full">
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!demo) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-2xl w-full">
          <div className="text-center py-8">
            <p className="text-gray-600">Demo not found</p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { payload } = demo || {};
  const { customer, dateTime, location: demoLocation, status, hostedDemo = {} } = payload || {};
  // If status is not 'cancelled' or 'completed', consider it scheduled
  const isScheduled = !['cancelled', 'completed'].includes(status?.toLowerCase() || '');

  // Check if the demo is overdue (scheduled and date has passed)
  const demoDate = dateTime?.demoDate || dateTime?.date;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to beginning of today
  
  const isOverdue = isScheduled && 
                   demoDate && 
                   new Date(demoDate) < today; // Only mark as overdue if before today
  
  // Check if demo was completed or cancelled in the last 7 days
  const isRecentlyCompletedOrCancelled = ['completed', 'cancelled'].includes(status?.toLowerCase() || '') &&
    (() => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      // Check if there's a timestamp or updatedAt field in the demo
      const demoTimestamp = demo?.payload?.updatedAt || demo?.payload?.timestamp || demo?.receivedAt;
      return demoTimestamp ? new Date(demoTimestamp.toDate ? demoTimestamp.toDate() : demoTimestamp) >= sevenDaysAgo : false;
    })();
  
  // Status badge styling
  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    if (isOverdue) return 'bg-red-100 text-red-800';
    
    switch (statusLower) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      default: // scheduled
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h3 className="text-xl font-semibold text-gray-900">Demo Details</h3>
          <div className="flex items-center space-x-2">
            {isScheduled && !['cancelled', 'completed'].includes((status || '').toLowerCase()) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Show the update modal
                  setShowUpdateModal(true);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Update Demo
              </button>
            )}
            {isRecentlyCompletedOrCancelled && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Show the reset modal
                  setShowResetModal(true);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Reset Demo
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 flex justify-between items-start">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Status</h4>
                <div className="flex items-center space-x-2">
                  <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
                    {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Scheduled'}
                  </span>
                  <span className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                    ID: {demo.id}
                  </span>
                  {isOverdue && (
                    <span className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800 animate-pulse">
                      Overdue
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Customer</h4>
              <p className="mt-1 text-sm text-gray-900">
                {customer?.name || payload?.customerName || 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Phone</h4>
              <p className="mt-1 text-sm text-gray-900">
                {customer?.mobile || customer?.phone || payload?.phone || 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Date</h4>
              <p className="mt-1 text-sm text-gray-900">
                {dateTime?.demoDate || dateTime?.date || payload?.date || 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Time Slot</h4>
              <p className="mt-1 text-sm text-gray-900">
                {dateTime?.timeSlot || dateTime?.time || payload?.timeSlot || 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Location</h4>
              <p className="mt-1 text-sm text-gray-900">
                {demoLocation || payload?.location || 'N/A'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Hosted Demo</h4>
              <p className="mt-1 text-sm text-gray-900">
                {hostedDemo?.isHosted === 'YES' ? 'Yes' : 'No'}
              </p>
            </div>
            
            {/* Show host information if demo is hosted */}
            {hostedDemo?.isHosted === 'YES' && (
              <>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Host Code</h4>
                  <p className="mt-1 text-sm text-gray-900 font-semibold text-purple-600">
                    {hostInfo?.code || hostedDemo?.hostCode || 'N/A'}
                  </p>
                </div>
                {hostInfo && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Host Name</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {hostInfo.name}
                    </p>
                  </div>
                )}
              </>
            )}
            
            {/* Show agent information */}
            <div>
              <h4 className="text-sm font-medium text-gray-500">Agent Code</h4>
              <p className="mt-1 text-sm text-gray-900 font-semibold text-indigo-600">
                {agentInfo?.code || payload?.agentCode || 'N/A'}
              </p>
            </div>
            {agentInfo && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Agent Name</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {agentInfo.name}
                </p>
              </div>
            )}
            
            {/* Show referrer information if present */}
            {referrerInfo && (
              <>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Referrer Code</h4>
                  <p className="mt-1 text-sm text-gray-900 font-semibold">
                    {referrerInfo.code}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Referrer Name</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {referrerInfo.name}
                  </p>
                </div>
              </>
            )}
          </div>
          
          {payload?.notes?.trim() && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {payload.notes.trim()}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end rounded-b-xl">
          <button
            type="button"
            className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:text-sm"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Demo Update Modal - Only shown when showUpdateModal is true */}
      {showUpdateModal && (
        <DemoUpdateModal
          isOpen={true}
          onClose={() => {
            setShowUpdateModal(false);
          }}
          demo={demo}
          onComplete={(outcome) => {
            // Refresh demo data after update if needed
            if (onUpdate) {
              onUpdate();
            }
            
            // Close the update modal
            setShowUpdateModal(false);

            // Special handling for orders: DemoUpdateModal will navigate to /orders
            // We just need to close this modal WITHOUT navigating to /my-demos
            if (outcome === 'placed_order' || outcome === 'set_upgrade') {
              setIsOpen(false);
              if (propOnClose) propOnClose();
            } else {
              // For other outcomes, use the standard close logic (which might navigate)
              handleClose();
            }
          }}
        />
      )}
      
      {/* Demo Reset Modal - Only shown when showResetModal is true */}
      {showResetModal && (
        <DemoResetModal
          demo={demo}
          onClose={() => {
            setShowResetModal(false);
          }}
          onSuccess={() => {
            // Refresh demo data after reset
            if (demoId) {
              getDoc(doc(db, 'demo_bookings', demoId)).then(doc => {
                if (doc.exists()) {
                  setDemo({ id: doc.id, ...doc.data() });
                }
              });
            }
            // Close the reset modal
            setShowResetModal(false);
          }}
        />
      )}
    </div>
  );
}
